import { useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { Mutation } from '@tanstack/react-query'
import { useOnline } from '@/hooks/useOnline'
import { isOutboxKey } from '@/lib/offline'

// Human labels for outbox key segments (['outbox', entity, op]) so the sync
// list reads like the thing the user did, not an API route.
const ENTITY_LABELS: Record<string, string> = {
  'work-orders': 'Work order',
  'work-order-parts': 'Work order part',
  'service-schedules': 'Service schedule',
  'service-plans': 'Service plan',
  'service-reminders': 'Service reminders',
  boats: 'Boat',
  'boat-images': 'Boat image',
  customers: 'Customer',
  deals: 'Deal',
  consignments: 'Consignment',
  slips: 'Slip',
  'storage-allocations': 'Storage allocation',
  transactions: 'Transaction',
  messages: 'Message',
  'message-templates': 'Message template',
  employees: 'Employee',
  roles: 'Role',
  users: 'User',
  sso: 'SSO settings',
  'parts-catalog': 'Catalog part',
  'parts-suppliers': 'Supplier',
  'purchase-orders': 'Purchase order',
  'parts-collection-runs': 'Price collection',
  'document-templates': 'Document template',
  documents: 'Document',
  registrations: 'Registration',
  'registration-items': 'Registration item',
}

const OP_LABELS: Record<string, string> = {
  create: 'create',
  update: 'edit',
  delete: 'delete',
  status: 'status change',
  send: 'send',
  'record-sale': 'record sale',
  'record-payout': 'record payout',
  withdraw: 'withdraw',
  assign: 'assign boat',
  vacate: 'vacate',
  rate: 'edit rate',
  apply: 'apply plan',
  run: 'reminder job',
  save: 'save',
  'reset-password': 'reset password',
  add: 'add line',
  logistics: 'logistics edit',
  generate: 'generate',
  patch: 'edit',
}

export function outboxLabel(mutationKey: readonly unknown[]): string {
  const entity = String(mutationKey[1] ?? '')
  const op = String(mutationKey[2] ?? '')
  return `${ENTITY_LABELS[entity] ?? entity} ${OP_LABELS[op] ?? op}`.trim()
}

export function isOutboxMutation(m: Mutation<unknown, Error, unknown, unknown>): boolean {
  return isOutboxKey(m.options.mutationKey)
}

// Queued offline writes: paused = waiting to sync, error = replay failed
// (e.g. duplicate email created in the meantime, expired session).
export type PendingChange = {
  mutation: Mutation<unknown, Error, unknown, unknown>
  label: string
  status: 'paused' | 'error'
  error?: string
}

function collectPending(queryClient: ReturnType<typeof useQueryClient>): PendingChange[] {
  return queryClient
    .getMutationCache()
    .getAll()
    .filter((m) => isOutboxMutation(m) && (m.state.isPaused || m.state.status === 'error'))
    .map((m) => ({
      mutation: m,
      label: outboxLabel(m.options.mutationKey ?? []),
      status: m.state.status === 'error' ? 'error' : 'paused',
      error: m.state.error instanceof Error ? m.state.error.message : undefined,
    }))
}

export function usePendingChanges(): PendingChange[] {
  const queryClient = useQueryClient()
  const [pending, setPending] = useState(() => collectPending(queryClient))
  useEffect(() => {
    return queryClient.getMutationCache().subscribe(() => {
      setPending(collectPending(queryClient))
    })
  }, [queryClient])
  return pending
}

export function useOfflineBanner(): {
  online: boolean
  queued: number
  failed: number
} {
  const online = useOnline()
  const pending = usePendingChanges()
  return useMemo(() => {
    const queued = pending.filter((p) => p.status === 'paused').length
    const failed = pending.filter((p) => p.status === 'error').length
    return { online, queued, failed }
  }, [online, pending])
}
